// components/notifications/NotificationCenterPopover.tsx
'use client';

import React, { memo, useEffect } from 'react';
import { toast } from 'sonner';
import { Bell, X } from '@/components/ui/lucide-memo';
import { Archive } from 'lucide-react';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore, useKernelStoreApi } from '@/kernel/kernelStores';
import { useNoticeDialog } from '@/components/portal/TrialNotice';
import styles from '@/components/portal/Notice.module.css';
import { notificationToastId, selectRecentNotifications, selectVisibleNotifications } from './notificationSelectors';
import type { KernelEntityRuntimeState } from '#types/types';

const formatNotificationTime = (createdAt: string): string => {
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }).format(date);
};

function NotificationCenterPopover({ onClose }: { onClose: () => void }) {
  const { kernel } = useKernel();
  const kernelStore = useKernelStoreApi();
  const { dialogRef, headingRef, headingId, onKeyDown } = useNoticeDialog();
  const isLoading = useKernelStore((state) => state.inventory.Notification.isLoading);
  const loadError = useKernelStore((state) => state.inventory.Notification.error);
  const entityState = useKernelStore((state) => state.entityKernelState);
  // READ ONCE SEEN (owner, 2026-10-05: 'remove this accept system. once it is read, it's read').
  // The centre lists recent notifications, read or not; opening it reads the unread ones.
  const notifications = useKernelStore((state) => selectRecentNotifications(state)
    .sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()));
  const listedCount = notifications.length;
  const unreadCount = useKernelStore((state) => selectVisibleNotifications(state).length);

  useEffect(() => {
    // Removing the focused row must not strand keyboard focus outside the modal.
    if (!dialogRef.current?.contains(document.activeElement)) headingRef.current?.focus();
  }, [listedCount]);

  useEffect(() => {
    void kernel.current?.refreshInventory('Notification', { includeArchived: false }, 50, { force: true }).catch(() => undefined);
  }, [kernel]);


  const acknowledge = async (notificationId: string, archive = false) => {
    const k = kernel.current;
    const userId = k?.getUserContext().dbUserId;
    if (!k || !userId || !(archive ? selectRecentNotifications : selectVisibleNotifications)(kernelStore.getState()).some(item => item.id === notificationId)) return;
    k.patchEntityRuntimeState('Notification', notificationId, { clearError: undefined });
    try {
      await k.updateEntity('Notification', notificationId, archive
        ? { archivedAt: new Date().toISOString() }
        : { readAt: new Date().toISOString() });
      if (k.getUserContext().dbUserId === userId) toast.dismiss(notificationToastId(userId, notificationId));
    } catch {
      if (k.getUserContext().dbUserId === userId) {
        const message = 'Could not clear the notification. Please try again.';
        k.patchEntityRuntimeState('Notification', notificationId, { clearError: message });
        k.handleError(message);
      }
    }
  };

  const markAllAsRead = async () => {
    const userId = kernel.current?.getUserContext().dbUserId;
    const items = selectVisibleNotifications(kernelStore.getState());
    let accountChanged = false;
    const unsubscribe = kernelStore.subscribe(state => {
      if (state.userContext.dbUserId !== userId) accountChanged = true;
    });
    try {
      for (let offset = 0; offset < items.length; offset += 10) {
        if (accountChanged || kernel.current?.getUserContext().dbUserId !== userId) return;
        await Promise.all(items.slice(offset, offset + 10).map(item => acknowledge(item.id)));
      }
    } finally {
      unsubscribe();
    }
  };

  useEffect(() => {
    if (unreadCount > 0) void markAllAsRead();
  }, [unreadCount]);

  const retryLoad = async () => {
    const k = kernel.current;
    const userId = k?.getUserContext().dbUserId;
    if (!k || !userId) return;
    try {
      await k.refreshInventory('Notification', { includeArchived: false, unreadOnly: true }, 50, { force: true, throwOnError: true });
    } catch {
      if (k.getUserContext().dbUserId === userId) k.handleError('Could not load notifications.');
    }
  };

  return (
    <dialog ref={dialogRef} aria-labelledby={headingId} className={styles.dialog + ' ' + styles.centre}
      onKeyDown={onKeyDown} onKeyUp={event => event.stopPropagation()}
      onCancel={event => { event.preventDefault(); onClose(); }}
      onClick={event => {
        if (event.target !== event.currentTarget) return;
        const box = event.currentTarget.getBoundingClientRect();
        if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) onClose();
      }}>
      <div className={styles.header}>
        <div>
          <h2 ref={headingRef} id={headingId} tabIndex={-1} className={styles.heading}>Notifications</h2>
          {loadError ? <p className={styles.meta}>Unable to load updates</p> : null}
        </div>
        <div className={styles.headerActions}>
          <button type="button" onClick={onClose} className={styles.iconButton} aria-label="Close notifications"><X size={18} /></button>
        </div>
      </div>
      <div className={styles.body}>
        {loadError && <div role="status">
          <p>Could not load notifications.</p>
          <button type="button" className={styles.button} onClick={() => { void retryLoad(); }}>Try again</button>
        </div>}
        {listedCount === 0 ? (
          !loadError && <div className={styles.empty} role="status">
            <Bell size={24} className={styles.emptyIcon} strokeWidth={1.5} />
            <p>{isLoading ? 'Loading notifications…' : 'You’re all caught up.'}</p>
            {!isLoading && <p className={styles.meta}>New updates will appear here.</p>}
          </div>
        ) : notifications.map(notification => {
          const clearError = (entityState['Notification:' + notification.id]?.state?.runtime as KernelEntityRuntimeState<'Notification'> | undefined)?.clearError;
          return (
          <article key={notification.id} className={styles.row}>
            <h3 className={styles.rowTitle}>{notification.title}</h3>
            {notification.body && <p className={styles.rowBody}>{notification.body}</p>}
            {clearError && <p role="alert">{clearError}</p>}
            <div className={styles.rowFooter}>
              <time className={styles.meta} dateTime={notification.createdAt}>{formatNotificationTime(notification.createdAt)}</time>
              <div className={styles.rowActions}>
                <button type="button" onClick={() => { void acknowledge(notification.id, true); }} className={styles.iconButton}
                  aria-label={'Archive notification: ' + notification.title} title="Archive notification"><Archive size={16} /></button>
              </div>
            </div>
          </article>
          );
        })}
      </div>
    </dialog>
  );
}

export default memo(NotificationCenterPopover);
